import type { CellContext, ColumnDef } from '@tanstack/react-table'
import type {
  Advertisement,
  AdvertisementStatus,
} from '../sponsorship/AdvertisementColumns'
import { Button } from '../ui/button'
import { ArrowUpDown } from 'lucide-react'
import { Badge } from '../ui/badge'
import { cn } from '@/lib/utils'
import {
  getAdvertisementStatusBadgeProps,
  transformAdvertisementPlacement,
} from '@/constants/sponsor'
import AdActionsDropdown from './AdActionsDropdown'
import { getPlainText } from '@/utils/helper'

interface ADColumnsOptions {
  onADNameClick?: (advertisement: Advertisement) => void
}

export const getadvertisementColumns = (
  options?: ADColumnsOptions
): ColumnDef<Advertisement>[] => {
  return [
    {
      accessorKey: 'adName',
      header: 'Ad Title',
      cell: ({ getValue, row }: CellContext<Advertisement, unknown>) => {
        const advertisement = row.original
        return (
          <button
            className="font-semibold text-ss-text-primary text-[14px] leading-4.5  hover:underline cursor-pointer text-left"
            onClick={() => options?.onADNameClick?.(advertisement)}
          >
            {getPlainText(getValue() as string)}
          </button>
        )
      },
    },
    {
      accessorKey: 'sponsorName',
      header: 'Sponsor Name',
      cell: ({ getValue }: { getValue: () => unknown }) => (
        <div className="font-medium text-ss-text-secondary text-[14px] leading-4.5 ">
          {getValue() as string}
        </div>
      ),
    },
    {
      accessorKey: 'eventName',
      header: 'Event Name',
      cell: ({ row }) => {
        const event = row.original
        return (
          <div className="flex flex-col gap-1">
            <div className="font-medium text-ss-text-secondary text-[14px] leading-4.5 ">
              {event?.eventName}
            </div>
            {event?.subEventName && (
              <div>
                <div className="font-normal text-ss-text-secondary text-[14px] leading-[22px]">
                  ( {event?.subEventName} )
                </div>
              </div>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'placement',
      header: 'Placement',
      cell: ({ getValue }: { getValue: () => unknown }) => (
        <div className="font-medium text-ss-text-secondary text-[14px] leading-4.5 ">
          {transformAdvertisementPlacement(getValue() as string)}
        </div>
      ),
    },
    {
      accessorKey: 'duration',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-auto p-0 hover:bg-transparent wrap-anywhere"
          >
            Duration
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        )
      },
      cell: ({ getValue }: { getValue: () => unknown }) => (
        <div className="font-medium text-ss-text-secondary text-[14px] leading-4.5 ">
          {getValue() as string}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as AdvertisementStatus
        const isChangesRequired = row.original.isChangesRequired
        const badgeProps = getAdvertisementStatusBadgeProps(
          status,
          isChangesRequired
        )
        return (
          <Badge
            variant="outline"
            className={cn(
              'px-2.5 py-1 text-xs font-medium rounded-full',
              badgeProps.className
            )}
          >
            {badgeProps.children}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'performance',
      header: 'Performance',
      cell: ({ getValue }: { getValue: () => unknown }) => {
        const perf = getValue() as {
          impressions: string
          clicks: string
          ctr: string
        }
        return (
          <div className="text-[14px]">
            <div className="font-medium text-ss-text-primary leading-4.5 ">
              {perf.impressions} impressions
            </div>
            <div className="font-normal text-ss-text-secondary leading-[22px]">
              {perf.clicks} clicks ({Number(perf.ctr)?.toFixed(1)}% CTR)
            </div>
          </div>
        )
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }: CellContext<Advertisement, unknown>) => {
        const ad = row.original
        return (
          <AdActionsDropdown
            adId={ad.id}
            adName={ad.adName}
            status={ad.status}
            sponsorShipId={ad.sponsorShipId}
            sponsorId={ad?.sponsorId}
          />
        )
      },
    },
  ]
}
